import { type JobDetail, type JobListItem, type NearbyJob, distanceLabel } from "@nmt/shared";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { errorMessage, get } from "../api";
import { cx } from "../components/ui";
import { uploadOne } from "../lib/upload";
import { useSession } from "../session";

type Shot = { id: string; file: File; url: string; takenAt: string; lat: number | null; lon: number | null; tags: string[]; state: "waiting" | "uploading" | "done" | "failed"; error?: string };
type JobPick = { id: string; label: string; detail: string };

const stamp = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}.${p(d.getMinutes())}.${p(d.getSeconds())}`;
};
/** The long side of a saved photo, in pixels (about 1 to 1.5 MB as JPEG). */
const SAVED_PX = 3072;
type Lens = { id: string; label: string; order: number };

/** The phone's back lenses by their names (iPhone: "Back Ultra Wide Camera", "Back Camera", "Back Telephoto Camera"). */
function backLenses(devices: MediaDeviceInfo[]): Lens[] {
  const back = devices.filter((d) => d.kind === "videoinput" && /back|rear|environment/i.test(d.label) && !/dual|triple/i.test(d.label));
  const out = back.map((d) => (/ultra ?wide/i.test(d.label) ? { id: d.deviceId, label: "0.5×", order: 0 } : /tele/i.test(d.label) ? { id: d.deviceId, label: "Zoom", order: 2 } : { id: d.deviceId, label: "1×", order: 1 }));
  // Only worth showing with more than one, and one of each.
  const one = [...new Map(out.sort((a, b) => a.order - b.order).map((l) => [l.order, l])).values()];
  return one.length > 1 ? one : [];
}

/**
 * The in-app camera: take photos one after another, straight to a job (they never go to the phone's
 * photo library). Each one uploads in the background with a small copy, the time, and where it was
 * taken. The job comes from the link, from where you're standing, or from a search.
 */
export function CameraPage() {
  const { me } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const video = useRef<HTMLVideoElement>(null);
  const fallback = useRef<HTMLInputElement>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [flash, setFlash] = useState(false);
  const [shots, setShots] = useState<Shot[]>([]);
  const [job, setJob] = useState<JobPick | null>(null);
  const [picking, setPicking] = useState(false);
  const [tag, setTag] = useState<string | null>(null);
  const [pos, setPos] = useState<{ lat: number; lon: number } | null>(null);
  const [nearby, setNearby] = useState<NearbyJob[] | null>(null);
  const count = useRef(0);
  const posRef = useRef(pos);
  posRef.current = pos;

  // The camera (the back one on phones). Started again after the phone sleeps (iOS stops it) or a lens is picked.
  const stream = useRef<MediaStream | null>(null);
  const [lenses, setLenses] = useState<Lens[]>([]);
  const [lens, setLens] = useState<string | null>(null);
  const [zoom, setZoom] = useState<{ min: number; max: number; value: number } | null>(null);
  const [digital, setDigital] = useState(1);
  const start = async (deviceId?: string) => {
    stream.current?.getTracks().forEach((t) => t.stop());
    setReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported");
      const s = await navigator.mediaDevices.getUserMedia({
        video: deviceId ? { deviceId: { exact: deviceId }, width: { ideal: 4096 }, height: { ideal: 3072 } } : { facingMode: { ideal: "environment" }, width: { ideal: 4096 }, height: { ideal: 3072 } },
        audio: false,
      });
      stream.current = s;
      const track = s.getVideoTracks()[0];
      const caps = (track?.getCapabilities?.() ?? {}) as { zoom?: { min: number; max: number } };
      setZoom(caps.zoom && caps.zoom.max > caps.zoom.min ? { min: caps.zoom.min, max: Math.min(caps.zoom.max, 10), value: (track?.getSettings() as { zoom?: number }).zoom ?? caps.zoom.min } : null);
      setDigital(1);
      if (video.current) {
        video.current.srcObject = s;
        await video.current.play().catch(() => undefined);
        setReady(true);
      }
      setCamError(null);
      // The back lenses, once allowed (their names only show after the first permission).
      if (!lenses.length) setLenses(backLenses(await navigator.mediaDevices.enumerateDevices()));
      if (track && !deviceId) setLens(track.getSettings().deviceId ?? null);
    } catch (e) {
      const name = (e as { name?: string }).name;
      setCamError(name === "NotAllowedError" ? "The camera isn't allowed for this site. Allow it in the browser's settings, or use the phone's camera below." : "This browser can't show the camera here. Use the phone's camera below.");
    }
  };
  useEffect(() => {
    void start();
    // Back from sleep or another app: the stream is dead or frozen, so start it again.
    const wake = () => {
      if (document.visibilityState !== "visible") return;
      const t = stream.current?.getVideoTracks()[0];
      if (!t || t.readyState === "ended" || t.muted || video.current?.paused) void start(lens ?? undefined);
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
      stream.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setZoomTo = (z: number) => {
    const t = stream.current?.getVideoTracks()[0];
    if (zoom && t) {
      const v = Math.max(zoom.min, Math.min(zoom.max, z));
      setZoom({ ...zoom, value: v });
      void t.applyConstraints({ advanced: [{ zoom: v } as MediaTrackConstraintSet] }).catch(() => undefined);
    } else setDigital(Math.max(1, Math.min(4, z)));
  };
  const zoomNow = zoom ? zoom.value : digital;
  // Pinch to zoom.
  const pinch = useRef<{ d: number; z: number } | null>(null);
  const touchDist = (e: React.TouchEvent) => Math.hypot(e.touches[0]!.clientX - e.touches[1]!.clientX, e.touches[0]!.clientY - e.touches[1]!.clientY);

  // Where we are: for the job suggestion and each photo's location.
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition((p) => setPos({ lat: p.coords.latitude, lon: p.coords.longitude }), () => undefined, { enableHighAccuracy: true, maximumAge: 30_000, timeout: 20_000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // The job: from the link (a job's "Take photos"), else the nearest open job.
  useEffect(() => {
    const id = params.get("job");
    if (id) void get<JobDetail>(`/api/jobs/${id}`).then((j) => setJob({ id: j.id, label: `#${j.number} ${j.customer.displayName}`, detail: [j.street, j.city].filter(Boolean).join(", ") }));
  }, [params]);
  // No job in the link: ask, with the jobs near here listed first.
  useEffect(() => {
    if (!params.get("job")) setPicking(true);
  }, [params]);
  useEffect(() => {
    if (!pos || nearby || params.get("job")) return;
    void get<NearbyJob[]>(`/api/jobs/nearby?lat=${pos.lat}&lon=${pos.lon}`)
      .then(setNearby)
      .catch(() => setNearby([]));
  }, [pos, nearby, params]);

  const add = (file: File, takenAt = new Date()) => {
    const p = posRef.current;
    setShots((s) => [...s, { id: crypto.randomUUID(), file, url: URL.createObjectURL(file), takenAt: takenAt.toISOString(), lat: p?.lat ?? null, lon: p?.lon ?? null, tags: tag ? [tag] : [], state: "waiting" }]);
  };

  const snap = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    // Kept at a reasonable size: big enough to scale for reports, exports, and sending; not 4K.
    // Digital zoom (when the camera has none of its own): the middle of the picture.
    const sw = v.videoWidth / digital;
    const sh = v.videoHeight / digital;
    const scale = Math.min(1, SAVED_PX / Math.max(sw, sh));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    canvas.getContext("2d")?.drawImage(v, (v.videoWidth - sw) / 2, (v.videoHeight - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height);
    const now = new Date();
    // Numbered, so photos taken in the same second get different names.
    const n = ++count.current;
    canvas.toBlob((b) => b && add(new File([b], `Photo ${stamp(now)} (${n}).jpg`, { type: "image/jpeg" }), now), "image/jpeg", 0.85);
    setFlash(true);
    setTimeout(() => setFlash(false), 120);
    navigator.vibrate?.(20);
  };

  // Uploads, one at a time in the order taken, once there's a job.
  const busy = shots.some((s) => s.state === "uploading");
  useEffect(() => {
    const next = shots.find((s) => s.state === "waiting");
    if (!job || busy || !next) return;
    const mark = (state: Shot["state"], error?: string) => setShots((all) => all.map((s) => (s.id === next.id ? { ...s, state, error } : s)));
    mark("uploading");
    uploadOne(`/api/jobs/${job.id}/files`, next.file, { tags: next.tags, takenAt: next.takenAt, latitude: next.lat, longitude: next.lon })
      .then(() => mark("done"))
      .catch((e) => mark("failed", errorMessage(e)));
  }, [shots, job, busy]);

  const left = shots.filter((s) => s.state === "waiting" || s.state === "uploading").length;
  const failed = shots.filter((s) => s.state === "failed");
  const done = () => {
    if (left && !window.confirm(`${left} photo${left === 1 ? " is" : "s are"} still uploading. Leave anyway? ${left === 1 ? "It" : "They"} won't be saved.`)) return;
    stream.current?.getTracks().forEach((t) => t.stop());
    shots.forEach((s) => URL.revokeObjectURL(s.url));
    if (job) navigate(`/jobs/${job.id}?tab=files`, { replace: true });
    else if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  // Pieces placed differently held upright (bars above and below) and sideways (a column on the right, like the phone's camera).
  const topBar = (
    <div className="flex items-center gap-3">
      <button className="min-h-11 shrink-0 rounded-full bg-black/50 px-4 py-2 text-sm font-semibold ring-1 ring-white/30 backdrop-blur active:bg-white/30" onClick={done}>
        {shots.length ? "Done" : "Close"}
      </button>
      <button className="min-h-11 min-w-0 max-w-full rounded-full bg-black/50 px-4 py-2 text-left text-sm ring-1 ring-white/30 backdrop-blur" onClick={() => setPicking(true)}>
        {job ? (
          <span className="block truncate">
            <span className="font-semibold">Job {job.label}</span> <span className="text-white/70">{job.detail}</span>
          </span>
        ) : (
          <span className="font-semibold text-yellow-300">Pick the job</span>
        )}
      </button>
    </div>
  );
  const tags = !!me?.lists.photoTags.length && (
    <div className="flex gap-2 overflow-x-auto">
      {me.lists.photoTags.map((t) => (
        <button key={t} className={cx("shrink-0 rounded-full px-3 py-1 text-sm font-medium backdrop-blur", tag === t ? "bg-white text-black" : "bg-black/40 text-white ring-1 ring-white/20")} onClick={() => setTag(tag === t ? null : t)}>
          {t}
        </button>
      ))}
    </div>
  );
  const shutter = (
    <button
      aria-label="Take photo"
      disabled={!ready}
      onClick={snap}
      className="size-[72px] shrink-0 rounded-full border-4 border-white bg-white/20 transition active:scale-95 active:bg-white/60 disabled:opacity-40"
    />
  );
  const last = shots.length > 0 && (
    <div className="flex items-center gap-2 landscape:flex-col landscape:gap-1 landscape:text-center">
      <img src={shots[shots.length - 1]!.url} alt="Last photo" className="size-12 rounded-md object-cover ring-2 ring-white/40" />
      <div className="text-xs leading-tight">
        <div className="font-semibold">{shots.length} photo{shots.length === 1 ? "" : "s"}</div>
        <div className="text-white/60">{left ? `uploading ${left}` : failed.length ? `${failed.length} failed` : job ? "all saved" : "waiting for job"}</div>
      </div>
    </div>
  );
  // Lenses when the phone lists them (iPhone), else the camera's own zoom, else digital zoom.
  const zoomSteps = zoom ? [zoom.min, 2, 3].filter((z, i, a) => z <= zoom.max && a.indexOf(z) === i) : [1, 2, 3];
  const zoomer = lenses.length ? (
    <div className="flex gap-1 rounded-full bg-black/40 p-1 ring-1 ring-white/20 backdrop-blur landscape:flex-col">
      {lenses.map((l) => (
        <button key={l.id} className={cx("min-h-9 min-w-9 rounded-full px-2 text-xs font-semibold", lens === l.id ? "bg-white text-black" : "text-white")} onClick={() => (setLens(l.id), void start(l.id))}>
          {l.label}
        </button>
      ))}
    </div>
  ) : (
    <div className="flex gap-1 rounded-full bg-black/40 p-1 ring-1 ring-white/20 backdrop-blur landscape:flex-col">
      {zoomSteps.map((z) => (
        <button key={z} className={cx("min-h-9 min-w-9 rounded-full px-2 text-xs font-semibold", Math.abs(zoomNow - z) < 0.05 ? "bg-white text-black" : "text-white")} onClick={() => setZoomTo(z)}>
          {z}×
        </button>
      ))}
    </div>
  );
  const phoneCamera = (
    <button className="rounded-full bg-white/10 px-3 py-1.5 text-xs" onClick={() => fallback.current?.click()}>
      Phone camera
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex h-[100dvh] w-[100dvw] touch-manipulation flex-col overflow-hidden bg-black text-white select-none landscape:flex-row">
      {/* Upright: the top bar above the picture */}
      <div className="px-3 pt-[max(env(safe-area-inset-top),0.75rem)] pb-2 landscape:hidden">{topBar}</div>

      {/* The viewfinder (sideways, the top bar and tags sit over it) */}
      <div
        className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
        onTouchStart={(e) => e.touches.length === 2 && (pinch.current = { d: touchDist(e), z: zoomNow })}
        onTouchMove={(e) => e.touches.length === 2 && pinch.current && setZoomTo(pinch.current.z * (touchDist(e) / pinch.current.d))}
        onTouchEnd={() => (pinch.current = null)}
      >
        <video ref={video} playsInline muted className="absolute inset-0 size-full object-cover" style={digital > 1 ? { transform: `scale(${digital})` } : undefined} />
        {ready && <div className="absolute bottom-3 left-1/2 -translate-x-1/2 portrait:bottom-3 landscape:top-1/2 landscape:right-3 landscape:bottom-auto landscape:left-auto landscape:-translate-x-0 landscape:-translate-y-1/2">{zoomer}</div>}
        {flash && <div className="absolute inset-0 bg-white/70" />}
        <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/50 to-transparent pt-[max(env(safe-area-inset-top),0.5rem)] pr-3 pb-6 pl-[max(env(safe-area-inset-left),0.75rem)] portrait:hidden">{topBar}</div>
        {tags && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent pt-6 pr-3 pb-[max(env(safe-area-inset-bottom),0.5rem)] pl-[max(env(safe-area-inset-left),0.75rem)] portrait:hidden">{tags}</div>}
        {camError && (
          <div className="absolute inset-0 grid place-items-center p-6 text-center">
            <div className="flex max-w-sm flex-col items-center gap-4">
              <p className="text-white/80">{camError}</p>
              <button className="rounded-full bg-white px-5 py-2.5 font-semibold text-black" onClick={() => fallback.current?.click()}>
                Use the phone's camera
              </button>
            </div>
          </div>
        )}
        {!job && shots.length > 0 && <div className="absolute inset-x-3 top-3 rounded-lg bg-black/70 px-3 py-2 text-center text-sm landscape:top-16">Photos wait here until you pick the job.</div>}
        {failed.length > 0 && (
          <div className="absolute inset-x-3 top-3 rounded-lg bg-red-700/90 px-3 py-2 text-center text-sm landscape:top-16">
            {failed.length} didn't upload ({failed[0]!.error}).{" "}
            <button className="font-semibold underline" onClick={() => setShots((all) => all.map((s) => (s.state === "failed" ? { ...s, state: "waiting", error: undefined } : s)))}>
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Upright: tags, then the last photo, shutter, and phone camera along the bottom */}
      {tags && <div className="px-3 pt-3 landscape:hidden">{tags}</div>}
      <div className="grid grid-cols-3 items-center px-5 pt-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] landscape:hidden">
        <div>{last}</div>
        <div className="flex justify-center">{shutter}</div>
        <div className="flex justify-end">{phoneCamera}</div>
      </div>

      {/* Sideways: a column on the right with the shutter in the middle, like the phone's camera */}
      <div className="box-content flex w-28 shrink-0 flex-col items-center justify-between py-[max(env(safe-area-inset-bottom),0.75rem)] pr-[env(safe-area-inset-right)] portrait:hidden">
        {phoneCamera}
        {shutter}
        <div className="flex min-h-12 items-center">{last}</div>
      </div>

      {/* The phone's own camera (when the in-page one isn't available); it doesn't save to the photo library either. */}
      <input
        ref={fallback}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          for (const f of e.target.files ?? []) add(f, new Date(f.lastModified || Date.now()));
          e.target.value = "";
        }}
      />

      {picking && <JobPicker nearby={nearby} onPick={(j) => (setJob(j), setPicking(false))} onClose={() => setPicking(false)} />}
    </div>
  );
}

/** Nearby open jobs first; or search any job by name, number, or address. */
function JobPicker({ nearby, onPick, onClose }: { nearby: NearbyJob[] | null; onPick: (j: JobPick) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<JobListItem[] | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) return setResults(null);
    const t = setTimeout(() => void get<{ items: JobListItem[] }>(`/api/jobs?status=all&q=${encodeURIComponent(q.trim())}`).then((r) => setResults(r.items.slice(0, 20))), 250);
    return () => clearTimeout(t);
  }, [q]);
  const row = (key: string, pick: JobPick) => (
    <li key={key}>
      <button className="w-full rounded-lg px-3 py-2.5 text-left hover:bg-mist" onClick={() => onPick(pick)}>
        <div className="font-semibold">Job {pick.label}</div>
        <div className="text-sm text-muted">{pick.detail}</div>
      </button>
    </li>
  );
  return (
    <div className="absolute inset-0 z-10 flex flex-col justify-end bg-black/50" onClick={onClose}>
      <div className="max-h-[80dvh] overflow-y-auto rounded-t-2xl bg-white p-4 pb-[max(env(safe-area-inset-bottom),1rem)] text-ink" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Which job?</h2>
          <button className="text-sm font-semibold text-muted" onClick={onClose}>
            Close
          </button>
        </div>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, job #, or address" className="mb-3 w-full rounded-lg border border-line px-3 py-2.5 text-base" />
        <ul>
          {results
            ? results.map((j) => row(j.id, { id: j.id, label: `#${j.number} ${j.customerName}${j.title ? ` · ${j.title}` : ""}`, detail: [j.street, j.city].filter(Boolean).join(", ") }))
            : nearby === null
              ? <li className="px-3 py-2 text-sm text-muted">Finding jobs near you…</li>
              : nearby.length
                ? [<li key="h" className="px-3 pt-1 pb-1 text-xs font-semibold uppercase tracking-wide text-muted">Near you</li>, ...nearby.map((j) => row(j.id, { id: j.id, label: `#${j.number} ${j.customerName}`, detail: `${distanceLabel(j.meters)} · ${j.address}` }))]
                : <li className="px-3 py-2 text-sm text-muted">No open jobs near here. Search above.</li>}
          {results?.length === 0 && <li className="px-3 py-2 text-sm text-muted">No jobs match.</li>}
        </ul>
      </div>
    </div>
  );
}
